/**
 * IAPHUB -> RevenueCat migration tracker (dashboard-facing read only).
 *
 * Fully generic: there is no app-id map here at all. `fetchMigrationStatus` calls the
 * backend for every selected application, and the backend answers `enabled: false` for
 * any application not explicitly placed in transition mode in the centralized backend
 * registry (revenuecat_apps.REVENUECAT_APPS). Today that's transition-mode applications
 * (IV Access, Pocus, US Pain, Interventional Pain, Anesthesia Assistant); VetRA and MSK
 * US Knee get `enabled: false` and this module renders nothing for them.
 *
 * This module never performs subscription-level matching and never calls RevenueCat or
 * IAPHUB directly — it only reads whatever the backend already computed and stored.
 */

const _toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizeSourceSnapshot = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    activeSubscriptions: _toNumber(raw.activeSubscriptions),
    revenue: _toNumber(raw.revenue),
    transactionCount: _toNumber(raw.transactionCount),
    snapshotAt: raw.snapshotAt || null,
  };
};

const normalizeAudit = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    iaphubActiveCount: _toNumber(raw.iaphubActiveCount),
    revenuecatActiveCount: _toNumber(raw.revenuecatActiveCount),
    confirmedOverlap: _toNumber(raw.confirmedOverlap),
    iaphubOnlyCount: _toNumber(raw.iaphubOnlyCount),
    coveragePercent: _toNumber(raw.coveragePercent),
    auditedAt: raw.auditedAt || null,
  };
};

/**
 * Normalize the backend's /migration/status payload. Returns a shape-stable object even
 * when disabled or when no snapshot/audit has been saved yet, so callers never need
 * defensive optional-chaining beyond `.enabled`.
 */
export const normalizeMigrationStatus = (raw) => {
  if (!raw || typeof raw !== 'object' || !raw.enabled) {
    return { enabled: false };
  }
  return {
    enabled: true,
    appId: raw.appId || null,
    appName: raw.appName || null,
    mode: raw.mode || null,
    primarySource: raw.primarySource || 'iaphub',
    notice: raw.notice || '',
    hasData: Boolean(raw.hasData),
    iaphub: normalizeSourceSnapshot(raw.iaphub),
    revenuecat: normalizeSourceSnapshot(raw.revenuecat),
    audit: normalizeAudit(raw.audit),
    lastSnapshotAt: raw.lastSnapshotAt || null,
    lastAuditAt: raw.lastAuditAt || null,
  };
};

/**
 * Fetch migration-tracking status for a dashboard application.
 *
 * Always calls the backend (no client-side app-id gate — the backend registry is the only
 * source of truth for which applications are in transition mode). Resolves to
 * `{ enabled: false }` on any error or when the API base URL is missing, never throws.
 */
export const fetchMigrationStatus = async (appId) => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  if (!baseUrl || !appId) return { enabled: false };

  try {
    const res = await fetch(`${baseUrl}/migration/status?${new URLSearchParams({ app_id: appId })}`);
    if (!res.ok) {
      console.warn(`Migration status unavailable for ${appId}: HTTP ${res.status}`);
      return { enabled: false };
    }
    return normalizeMigrationStatus(await res.json());
  } catch (error) {
    console.error(`Error fetching migration status for ${appId}:`, error);
    return { enabled: false };
  }
};

export default fetchMigrationStatus;
